import type { DividerSlotsAndSlotProps } from '@mui/joy/Divider';
import BaseDivider from '@mui/joy/Divider';
import { forwardRef, memo, useMemo } from 'react';
import { slowCn } from '../../lib/utils';
import { getUtilityColor } from '../../utils/getUtilityColor';
import type { DividerProps } from './types';
import { dashedOrDottedDividerStyles } from './utils/dashedOrDottedDividerStyles';

const colorVariableMap = {
  // todo: check if this is breaking
  'border-transparent': 'transparent',
  'border-brand-secondary': 'var(--border-brand-secondary)',
  'border-brand-solid': 'var(--border-brand-solid)',
  'border-primary': 'var(--border-primary)',
  'border-secondary': 'var(--border-secondary)',
  'border-tertiary': 'var(--border-tertiary)',
  'border-success': 'var(--border-success)',
  'border-warning': 'var(--border-warning)',
  'border-error': 'var(--border-error)',
  'border-warning-solid': 'var(--border-warning-solid)',
  'border-success-solid': 'var(--border-success-solid)',
  'border-error-solid': 'var(--border-error-solid)',
  'border-orange-solid': 'var(--palette-orange-600)',
  'border-lime-green-solid': 'var(--palette-lime-green-600)',
  'border-cyan-solid': 'var(--palette-cyan-600)',
  'border-gray-solid': 'var(--palette-gray-600)',
  'border-violet-solid': 'var(--palette-violet-600)',
};

// Sort by length to match the longest color variable first , for exact match
// e.g. border-primary-solid should match before border-primary
const sortedKeys = Object.keys(colorVariableMap).sort((a, b) => b.length - a.length);

const Divider = forwardRef<HTMLHRElement, DividerProps>((props, ref) => {
  const {
    className,
    color = 'border-tertiary',
    style,
    orientation = 'horizontal',
    dividerType = 'solid',
    ...rest
  } = props;

  const colorVariableInClassName = useMemo(() => {
    const matchFromSortedKeys = sortedKeys.find((colorVariable) =>
      className?.includes(colorVariable),
    ) as DividerProps['color'];
    if (matchFromSortedKeys) {
      return matchFromSortedKeys;
    }

    const utilityColor = getUtilityColor('border', className);
    if (utilityColor) {
      return utilityColor;
    }
  }, [className]);

  const dashedOrDottedBorderStyle = useMemo(() => {
    return dashedOrDottedDividerStyles(className, style, orientation, dividerType);
  }, [className, style, orientation, dividerType]);

  const dividerSlotProps = useMemo<DividerSlotsAndSlotProps['slotProps']>(
    () => ({
      root: {
        className: slowCn(
          'h-[unset]',
          className,
          props.children ? 'text-secondary' : 'text-transparent',
        ),
        style: {
          '--Divider-lineColor':
            colorVariableMap[colorVariableInClassName ?? color] ??
            `var(--${colorVariableInClassName})`,
          ...dashedOrDottedBorderStyle,
          '--Divider-thickness': props.orientation === 'horizontal' ? style?.height : style?.width,
          '&::before, &::after': {
            ...(props.orientation === 'horizontal' ? { height: '100%' } : { width: '100%' }),
          },
          ...style,
        },
      },
    }),
    [
      className,
      color,
      colorVariableInClassName,
      dashedOrDottedBorderStyle,
      props.children,
      style,
      props.orientation,
    ],
  );

  return <BaseDivider orientation={orientation} ref={ref} slotProps={dividerSlotProps} {...rest} />;
});

Divider.displayName = 'Divider';

export default memo(Divider);
